import type { TFunction } from 'i18next'

import type { CaptureItem, CaptureResult } from '@/hooks/use-voice-goals'

// Past captures (stored rows) have no events count.
export function countSummary(t: TFunction, c: Omit<CaptureResult, 'events'> & { events?: number }) {
  return [
    c.goals > 0 && t('voice.countGoals', { count: c.goals }),
    !!c.events && t('voice.countEvents', { count: c.events }),
    c.tasks > 0 && t('voice.countTasks', { count: c.tasks }),
    c.habits > 0 && t('voice.countHabits', { count: c.habits }),
  ]
    .filter(Boolean)
    .join(' · ')
}

export function countItems(items: CaptureItem[]): CaptureResult {
  return {
    goals: items.filter((i) => i.kind === 'goal').length,
    tasks: items.filter((i) => i.kind === 'task').length,
    events: items.filter((i) => i.kind === 'event').length,
    habits: items.filter((i) => i.kind === 'habit').length,
  }
}

export function formatDuration(t: TFunction, ms: number) {
  const secs = Math.max(1, Math.round(ms / 1000))
  return secs < 60
    ? t('voice.durationSeconds', { count: secs })
    : t('voice.durationMinutes', { count: Math.round(secs / 60) })
}

export function formatTimer(ms: number) {
  const secs = Math.floor(ms / 1000)
  return `${Math.floor(secs / 60)}:${String(secs % 60).padStart(2, '0')}`
}

export function isMacPlatform() {
  if (typeof navigator === 'undefined') return false
  const platform =
    (navigator as Navigator & { userAgentData?: { platform?: string } }).userAgentData?.platform ??
    navigator.platform ??
    ''
  return /mac|iphone|ipad|ipod/i.test(platform)
}

export const NEW_GOAL_SHORTCUT_LABEL = 'G'
export const NEW_EVENT_SHORTCUT_LABEL = 'E'

function isBareKey(e: KeyboardEvent, key: string) {
  if (e.isComposing || e.keyCode === 229 || e.repeat) return false
  // Autofill and password managers dispatch keydown events with no `key`.
  if (e.metaKey || e.ctrlKey || e.altKey || e.key?.toLowerCase() !== key) return false
  const target = e.target as HTMLElement | null
  if (!target) return true
  return !(
    target.tagName === 'INPUT' ||
    target.tagName === 'TEXTAREA' ||
    target.tagName === 'SELECT' ||
    target.isContentEditable
  )
}

// ⌘B/Ctrl+B also works inside text fields; the app has no rich text, so bold isn't lost.
export function captureFieldShortcutLabel(mac = isMacPlatform()) {
  return mac ? '⌘B' : 'Ctrl+B'
}

function isFieldCaptureCombo(e: KeyboardEvent, mac = isMacPlatform()) {
  if (e.isComposing || e.repeat || e.code !== 'KeyB' || e.altKey || e.shiftKey) return false
  return mac ? e.metaKey && !e.ctrlKey : e.ctrlKey && !e.metaKey
}

export const isCaptureHotkey = (e: KeyboardEvent) => isBareKey(e, 'b') || isFieldCaptureCombo(e)
export const isNewGoalHotkey = (e: KeyboardEvent) => isBareKey(e, 'g')
export const isNewTaskHotkey = (e: KeyboardEvent) => isBareKey(e, 't')
export const isNewHabitHotkey = (e: KeyboardEvent) => isBareKey(e, 'h')
export const isNewEventHotkey = (e: KeyboardEvent) => isBareKey(e, 'e')
